import { NgModule } from '@angular/core';
import { CdkTreeModule } from '@angular/cdk/tree';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule, MatRippleModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorDefaultOptions, MatPaginatorModule, MAT_PAGINATOR_DEFAULT_OPTIONS } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTreeModule } from '@angular/material/tree';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { PipesModule } from '../theme/pipes/pipes.module';
import { DirectivesModule } from '../theme/directives/directives.module'; 

import { MatOverlayComponent } from './overlay/mat-overlay.component';
import { LoadMoreComponent } from './load-more/load-more.component';
import { LogoComponent } from './logo/logo.component';
import { ConfirmationDialogComponent } from './dialog/confirmation-dialog.component';
import { AvatarComponent } from './avatar/avatar.component';
import { ChangePasswordDialogComponent } from './dialog/change-password-dialog.component';
import { AddClientDialogComponent } from './dialog/add-client-dialog.component';
import { ShowFileDialogComponent } from './dialog/show-file-dialog/show-file-dialog.component';
import { MoovsInProcessTableComponent } from './moovs-in-process-table/moovs-in-process-table.component';
import { EditMetadataDialogComponent } from './dialog/edit-metadata-dialog.component';
import { TextEditorComponent } from './text-editor/text-editor.component';

import { TextEditorPreviewDialogComponent } from './dialog/text-editor-preview-dialog.component';
import { EditRowComponent } from './edit-header-footer-rows/edit-row/edit-row.component';
import { EditHeaderFooterRowsComponent } from './edit-header-footer-rows/edit-header-footer-rows.component';
import { QuillModule } from 'ngx-quill';
import Quill from 'quill';
import { EditFileNamingComponent } from './edit-file-naming/edit-file-naming.component';
import { ImportDefinitionDialogComponent } from './dialog/import-definition-dialog.component';
import { TestAIPromptDialogComponent } from './dialog/test-ai-prompt-dialog.component';
import { ExternalUploadLinkDialogComponent } from './dialog/external-upload-link-dialog.component';
import { EditNodeFunctionDialogComponent } from './dialog/edit-node-function-dialog.component';
import { BrandImageCropperComponent } from './brand-image-cropper/brand-image-cropper.component';
import { NgxColorsModule } from 'ngx-colors';
import { ColorPickerComponent } from './color-picker/color-picker.component';
import { ActivateAdoptionDialogComponent } from './dialog/activate-adoption-dialog.component';
import { DashboardCounterComponent } from './dashboard-counter/dashboard-counter.component';
import { SelectTransformationProtocolComponent } from './dialog/select-transformation-protocol-dialog.component';
import { ExecutePremierIntegrationDialogComponent } from './dialog/execute-premier-integration-dialog.component';
import { SelectFilterProtocolComponent } from './dialog/select-filter-protocol-dialog.component';
import { SelectValidationProtocolComponent } from './dialog/select-validation-protocol-dialog.component';
import { ShowFileValidationDialogComponent } from './dialog/show-file-validation-dialog.component';
import { SelectAllocationProtocolComponent } from './dialog/select-allocation-protocol-dialog.component';
import { QbDisconnectDialogComponent } from './dialog/qb-disconnect-dialog.component';
import { QBAccountsDialogComponent } from './dialog/qbAccounts-dialog.component';
import { DefaultMetadataDialogComponent } from './dialog/default-metadata-dialog.component';
import { MoovsInTestingStatusTableComponent } from './moovs-in-testing-status-table/moovs-in-testing-status-table.component';
import { EditFeeScheduleDialogComponent } from './dialog/edit-fee-schedule-dialog.component';
import { ShowFileComponent } from './dialog/show-file-dialog/show-file/show-file.component';
import { XeroDisconnectDialogComponent } from './dialog/xero-disconnect-dialog.component';
import { MicrosoftDisconnectDialogComponent } from './dialog/microsoft-disconnect-dialog.component';
import { XeroAccountsDialogComponent } from './dialog/xeroAccounts-dialog.component';
import { EditAdoptionDialogComponent } from './dialog/edit-adoption-dialog.component';
import { MoovExecutionStatusDialogComponent } from './dialog/moov-execution-status-dialog.component';
import { FileSharingDisconnectDialogComponent } from './dialog/filesharing-disconnect-dialog.component';
import { MissingRequiredParametersDialogComponent } from './dialog/missing-required-parameters-dialog.component';
import { DropboxDisconnectDialogComponent } from './dialog/dropbox-disconnect-dialog.component';
import { TransformationDefinitionConfirmationDialogComponent } from './dialog/transformation-definition-confirmation-dialog.component';
import { MFADialogComponent } from './dialog/mfa-dialog.component';
import { MFARecoveryDialogComponent } from './dialog/mfa-recovery-dialog.component';
import { ReportChartComponent } from '../pages/home/report-chart/report-chart.component';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { MatSpinnerOverlayModule } from './spinner/mat-spinner-overlay.module';
import { MetaDataDialogComponent } from './dialog/meta-data-dialog.component';
import { EditPresetDialogComponent } from './dialog/edit-preset-dialog.component';
import { EditKVPListComponent } from './edit-kvplist/edit-kvplist.component';
import { EditPresetDefaultsDialogComponent } from './dialog/edit-preset-defaults-dialog.component';
import { EditCustomPriceDialogComponent } from './dialog/edit-custom-price-dialog.component';
import { WizardExitDialogComponent } from './dialog/wizard-exit-dialog.component';

const Size = Quill.import("attributors/style/size") as any;
const Align = Quill.import("attributors/style/align") as any;
const Link = Quill.import('formats/link') as any;

Link.sanitize = function(url:string){
  if(!url.startsWith('https://') && (!url.startsWith('http://'))){
    return `https://${url}`;
  }else{
    return url;
  }
};

Quill.register(Size, true);
Quill.register(Align, true);
Quill.register(Link, true);

const modules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],        // toggled buttons

    [{ 'list': 'ordered'}, { 'list': 'bullet' }],

    [{ 'indent': '-1'}, { 'indent': '+1' }],          // outdent/indent

    [{ 'size': ['small', false, 'large', 'huge'] }],  // custom dropdown
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],

    [{ 'color': [] }, { 'background': [] }],          // dropdown with defaults from theme
    [{ 'font': [] }],
    [{ 'align': [] }]
  ]
};

const appearance: MatPaginatorDefaultOptions = {
  formFieldAppearance: 'fill'
};

@NgModule({
  imports: [
    CdkTreeModule,
    ScrollingModule,
    CommonModule,
    DragDropModule,
    FormsModule,
    RouterModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatBadgeModule,
    MatBottomSheetModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDatepickerModule,
    MatDialogModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatGridListModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatMenuModule,
    MatNativeDateModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatRadioModule,
    MatRippleModule,
    MatSelectModule,
    MatSidenavModule,
    MatSliderModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatSortModule,
    MatSpinnerOverlayModule,
    MatTableModule,
    MatTabsModule,
    MatToolbarModule,
    MatTooltipModule,
    MatTreeModule,
    MatStepperModule,
    ReportChartComponent,
    PipesModule,
    DirectivesModule,
    QuillModule.forRoot({modules}),
    //ImageCropperModule,
    BrandImageCropperComponent,
    NgxColorsModule
  ],
  exports: [
    CdkTreeModule,
    ScrollingModule,
    DragDropModule,
    RouterModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatBadgeModule,
    MatBottomSheetModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDatepickerModule,
    MatDialogModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatGridListModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatMenuModule,
    MatNativeDateModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatRadioModule,
    MatRippleModule,
    MatSelectModule,
    MatSidenavModule,
    MatSliderModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatSortModule,
    MatTableModule,
    MatTabsModule,
    MatToolbarModule,
    MatTooltipModule,
    MatTreeModule,
    MatStepperModule,
    ReportChartComponent,
    PipesModule,
    DirectivesModule,
    MatSpinnerOverlayModule,
    MatOverlayComponent,
    LogoComponent,
    LoadMoreComponent,
    ConfirmationDialogComponent,
    ChangePasswordDialogComponent,
    AddClientDialogComponent,
    ShowFileDialogComponent,
    ShowFileComponent,
    ShowFileValidationDialogComponent,
    AvatarComponent,
    MoovsInProcessTableComponent,
    TextEditorComponent,
    TextEditorPreviewDialogComponent,
    EditRowComponent,
    EditHeaderFooterRowsComponent,
    EditFileNamingComponent,
    ImportDefinitionDialogComponent,
    TestAIPromptDialogComponent,
    ExternalUploadLinkDialogComponent,
    EditNodeFunctionDialogComponent,
    BrandImageCropperComponent,
    //ImageCropperModule,
    NgxColorsModule,
    ColorPickerComponent,
    DashboardCounterComponent,
    SelectTransformationProtocolComponent,
    SelectFilterProtocolComponent,
    SelectValidationProtocolComponent,
    SelectAllocationProtocolComponent,
    QbDisconnectDialogComponent,
    QBAccountsDialogComponent,
    DefaultMetadataDialogComponent,
    MoovsInTestingStatusTableComponent,
    EditFeeScheduleDialogComponent,
    XeroDisconnectDialogComponent,
    XeroAccountsDialogComponent,
    MicrosoftDisconnectDialogComponent,
    EditAdoptionDialogComponent,
    MoovExecutionStatusDialogComponent,
    FileSharingDisconnectDialogComponent,
    DropboxDisconnectDialogComponent,
    MissingRequiredParametersDialogComponent,
    TransformationDefinitionConfirmationDialogComponent,
    MFADialogComponent,
    MFARecoveryDialogComponent,
    MetaDataDialogComponent,
    EditPresetDialogComponent,
    EditPresetDefaultsDialogComponent,
    EditKVPListComponent,
    EditCustomPriceDialogComponent,
    WizardExitDialogComponent
  ],
  declarations: [
    MatOverlayComponent,
    LogoComponent,
    LoadMoreComponent,
    ConfirmationDialogComponent,
    ChangePasswordDialogComponent,
    AddClientDialogComponent,
    ShowFileDialogComponent,
    ShowFileComponent,
    ShowFileValidationDialogComponent,
    AvatarComponent,
    MoovsInProcessTableComponent,
    EditMetadataDialogComponent,
    TextEditorComponent,
    TextEditorPreviewDialogComponent,
    EditRowComponent,
    EditHeaderFooterRowsComponent,
    EditFileNamingComponent,
    ImportDefinitionDialogComponent,
    TestAIPromptDialogComponent,
    ExternalUploadLinkDialogComponent,
    EditNodeFunctionDialogComponent,
    ColorPickerComponent,
    ActivateAdoptionDialogComponent,
    DashboardCounterComponent,
    SelectTransformationProtocolComponent,
    SelectFilterProtocolComponent,
    SelectValidationProtocolComponent,
    SelectAllocationProtocolComponent,
    ExecutePremierIntegrationDialogComponent,
    QbDisconnectDialogComponent,
    QBAccountsDialogComponent,
    DefaultMetadataDialogComponent,
    MoovsInTestingStatusTableComponent,
    EditFeeScheduleDialogComponent,
    XeroDisconnectDialogComponent,
    XeroAccountsDialogComponent,
    MicrosoftDisconnectDialogComponent,
    EditAdoptionDialogComponent,
    MoovExecutionStatusDialogComponent,
    FileSharingDisconnectDialogComponent,
    DropboxDisconnectDialogComponent,
    MissingRequiredParametersDialogComponent,
    TransformationDefinitionConfirmationDialogComponent,
    MFADialogComponent,
    MFARecoveryDialogComponent,
    MetaDataDialogComponent,
    EditPresetDialogComponent,
    EditPresetDefaultsDialogComponent,
    EditKVPListComponent,
    EditCustomPriceDialogComponent,
    WizardExitDialogComponent
  ],
  providers: [
    {
      provide: MAT_PAGINATOR_DEFAULT_OPTIONS,
      useValue: appearance
    },
    provideCharts(withDefaultRegisterables()),

  ]
})
export class SharedModule { 
}
